import React, { FC, memo, useMemo } from 'react'
import {
  getNextRandomColor,
  resetRandomColors,
} from '@ucheba/utils/helpers/random-colors'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { sv } from '@ucheba/utils/helpers/styles/variables'
import { Text } from '@ucheba/ui/components/Text/desktop'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { Button } from '@ucheba/ui/components/Button/desktop'
import { Banner } from '../../../Banner/desktop'
import { IEventPageProps } from '../../types'
import { useEventsListItemCore } from '../../../EventsList/bll'
import { headerHeight } from '../../../Header/constants'
import { socialText } from '../../constants'
import EventsContent from '../../components/EventsContent/desktop'
import EventsLeading from '../../components/EventsLeading/desktop'
import EventsLocation from '../../components/EventsLocation/desktop'
import EventsAnother from '../../components/EventsAnother/desktop'
import EventHeroBrandingUcheba from '../../components/EventHeroBrandingUcheba/desktop'
import EventBrandingUchebaRegisration from '../../components/EventBrandingUchebaRegisration/desktop'
import ParentFormDialog from '../../../ParentFormDialog/desktop'
import ParentFormDialogSuccess from '../../../ParentFormDialog/desktop/ParentFormDialogSuccess'

const TopBlock = styled.div`
  background-color: ${(props) => sv.color[props.color]};
  height: ${headerHeight.desktop}px;
  margin-top: -${headerHeight.desktop}px;
`

const LinkAgreement = styled.a`
  color: #1d8afe;
  text-decoration: underline;
`

const EventPageBrandingFinished: FC<IEventPageProps> = (props) => {
  const {
    institution,
    content,
    speakers,
    addresses,
    branding,
    streamUrl,
    pathname,
    eventsSearchPath,
    bannerProgramTypes,
    templateId,
  } = props

  const { onClickRegistration, messengersDialog, buttonData, registersPending } =
    useEventsListItemCore({
      ...props,
    })

  resetRandomColors()

  const locations = addresses.map((address) => ({
    address: address.address,
    comment: address.comment,
    point: address.point,
    color: getNextRandomColor(),
  }))

  const { query } = useRouter()

  const params = useMemo(() => {
    if (query['programTypes[]']) {
      return query
    }

    if (!bannerProgramTypes) return undefined

    return {
      ...query,
      programTypes: bannerProgramTypes,
    }
  }, [query, bannerProgramTypes])

  return (
    <>
      <TopBlock color={branding?.headerBackgroundColor} />

      <EventHeroBrandingUcheba {...props} isFinished />

      <Container>
        <Grid as='main' justifyContent='center'>
          <GridCol uxs={14}>
            {content && <EventsContent content={content} />}

            <Spacing spacing='xlarge' />

            {speakers && <EventsLeading speakers={speakers} />}

            {locations && <EventsLocation locations={locations} />}

            <Spacing spacing='xlarge' />

            <EventsAnother pathname={pathname} eventsSearchPath={eventsSearchPath} />
          </GridCol>

          <GridCol uxs={10} as='aside'>
            <Spacing spacing='uxlarge' />

            {buttonData.eventRegisteredStatus === 'isNotRegistered' && (
              <div style={{ position: 'relative', height: '100%' }}>
                <div style={{ position: 'sticky', top: '24px' }}>
                  <EventBrandingUchebaRegisration
                    {...props}
                    onClickRegistration={onClickRegistration}
                    title={
                      'Зарегистрируйтесь<br/> на мероприятие и <span style="color: #ff6a00">получите его запись</span>'
                    }
                    subTitle=''
                    loading={registersPending}
                  />

                  <Spacing spacing='small' />

                  <div style={{ padding: '0 24px' }}>
                    <Text
                      color='gray60'
                      fontSize='xsmall1'
                      lineHeight='xsmall1'
                      textAlign='center'
                      html={false}
                    >
                      Нажимая на кнопку «Отправить», я принимаю{' '}
                      <LinkAgreement href='/agreement' target='_blank'>
                        условия обработки персональных данных
                      </LinkAgreement>
                    </Text>
                  </div>
                </div>
              </div>
            )}

            {buttonData.eventRegisteredStatus === 'isRegistered' && (
              <div style={{ padding: '0 16px' }}>
                <Button color='blue' block>
                  Вы зарегистрированы на мероприятие
                </Button>
              </div>
            )}

            {buttonData.eventRegisteredStatus === 'watchBroadcast' && (
              <Button color='blue' href={streamUrl} block target='_blank'>
                Смотреть запись
              </Button>
            )}
          </GridCol>
        </Grid>

        <Spacing spacing='uxxlarge' />
      </Container>

      {institution && (
        <>
          <Banner id={6} extraParams={params} />

          <Banner id={14} extraParams={params} />
        </>
      )}

      <ParentFormDialog exam={props?.exam} id={props?.id} />

      <ParentFormDialogSuccess
        open={messengersDialog.isActive}
        templateId={templateId}
        onClose={messengersDialog.closeDialog}
        isInstitution={!!institution}
        description={socialText.finished.description}
      />
    </>
  )
}

export default memo(EventPageBrandingFinished)
